import { FilePen, Receipt, ShoppingBag, ShoppingCart, Undo2 } from 'lucide-react';

import { defineTour, type TourResolver } from '@nucleus-commerce/admin-kit/tour';
import {
  getAdminV1StoresStoreIdCheckouts,
  getAdminV1StoresStoreIdDraftOrders,
  getAdminV1StoresStoreIdReturns,
} from '@nucleus-commerce/api-client/admin';

import { orderForDetail } from './resolvers';

export const ORDER_DETAIL_ROUTE = '/_authed/_active/s/$storeId/orders/$orderId';

export const ordersListTour = defineTour({
  id: 'orders.list',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/orders/',
  scope: 'store',
  requires: 'orders.read',
  icon: ShoppingCart,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [{ target: 'orders.list.table' }, { target: 'orders.list.empty', copy: 'empty' }],
    },
    {
      id: 'search',
      kind: 'action',
      target: 'orders.list.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'filters', kind: 'explain', target: 'orders.list.filters', optional: true },
    { id: 'badges', kind: 'explain', target: 'orders.list.status-badges', optional: true },
    {
      id: 'bulk',
      kind: 'explain',
      target: 'orders.list.table',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'export',
      kind: 'explain',
      target: 'orders.list.import-export',
      requires: 'exports.run',
      optional: true,
    },
  ],
  related: ['orders.detail', 'production.board'],
});

export const orderDetailTour = defineTour({
  id: 'orders.detail',
  version: 1,
  group: 'orders',
  route: ORDER_DETAIL_ROUTE,
  scope: 'record',
  resolve: orderForDetail,
  requires: 'orders.read',
  icon: Receipt,
  steps: [
    { id: 'badges', kind: 'explain', target: 'orders.detail.status-badges' },
    { id: 'lines', kind: 'explain', target: 'orders.detail.lines', placement: 'right' },
    { id: 'totals', kind: 'explain', target: 'orders.detail.totals' },
    { id: 'fulfilments', kind: 'explain', target: 'orders.detail.fulfilments' },
    { id: 'customer', kind: 'explain', target: 'orders.detail.customer', placement: 'left' },
    { id: 'notes', kind: 'explain', target: 'orders.detail.notes', placement: 'left' },
    { id: 'timeline', kind: 'explain', target: 'orders.detail.timeline', placement: 'top' },
  ],
  related: ['orders.detail-actions', 'production.card'],
});

export const orderActionsTour = defineTour({
  id: 'orders.detail-actions',
  version: 1,
  group: 'orders',
  route: ORDER_DETAIL_ROUTE,
  scope: 'record',
  resolve: orderForDetail,
  requires: 'orders.read',
  icon: Receipt,
  steps: [
    {
      id: 'mark-paid',
      kind: 'explain',
      target: 'orders.detail.mark-paid',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'fulfil',
      kind: 'explain',
      target: 'orders.detail.fulfil',
      requires: 'fulfillments.write',
      optional: true,
    },
    {
      id: 'refund',
      kind: 'explain',
      target: 'orders.detail.refund',
      requires: 'orders.refund',
      optional: true,
    },
    {
      id: 'cancel',
      kind: 'explain',
      target: 'orders.detail.cancel',
      requires: 'orders.cancel',
      optional: true,
    },
    {
      id: 'open-return',
      kind: 'explain',
      target: 'orders.detail.open-return',
      requires: 'returns.write',
      optional: true,
    },
    { id: 'more-menu', kind: 'action', target: 'orders.detail.more-menu' },
    {
      id: 'send-design',
      kind: 'explain',
      target: 'orders.detail.send-design',
      requires: 'orders.write',
      optional: true,
      waitMs: 1500,
    },
  ],
  related: ['production.card', 'returns.detail'],
});

const newestDraftOrder: TourResolver = {
  key: 'newest-draft-order',
  thing: 'things.draftOrder',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdDraftOrders({ storeId }, { limit: 1 });
    const draft = page.items[0];
    return draft ? { storeId, draftId: draft.id } : null;
  },
};

const newestAbandonedCheckout: TourResolver = {
  key: 'newest-abandoned-checkout',
  thing: 'things.abandonedCheckout',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdCheckouts({ storeId }, { limit: 1 });
    const checkout = page.items[0];
    return checkout ? { storeId, checkoutId: checkout.id } : null;
  },
};

const newestReturn: TourResolver = {
  key: 'newest-return',
  thing: 'things.return',
  load: async ({ storeId }) => {
    if (!storeId) return null;
    const page = await getAdminV1StoresStoreIdReturns({ storeId }, { limit: 1 });
    const found = page.items[0];
    return found ? { storeId, returnId: found.id } : null;
  },
};

export const draftOrdersListTour = defineTour({
  id: 'draft-orders.list',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/draft-orders/',
  scope: 'store',
  requires: 'orders.read',
  icon: FilePen,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'draft-orders.list.table' },
        { target: 'draft-orders.list.empty', copy: 'empty' },
      ],
    },
    { id: 'new', kind: 'explain', target: 'draft-orders.list.new', requires: 'orders.write' },
  ],
  related: ['draft-orders.detail'],
});

export const draftOrderDetailTour = defineTour({
  id: 'draft-orders.detail',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/draft-orders/$draftId',
  scope: 'record',
  resolve: newestDraftOrder,
  requires: 'orders.read',
  icon: FilePen,
  steps: [
    { id: 'lines', kind: 'explain', target: 'draft-orders.detail.lines', placement: 'right' },
    { id: 'customer', kind: 'explain', target: 'draft-orders.detail.customer', placement: 'left' },
    { id: 'discount', kind: 'explain', target: 'draft-orders.detail.discount' },
    { id: 'totals', kind: 'explain', target: 'draft-orders.detail.totals', placement: 'top' },
    {
      id: 'send-invoice',
      kind: 'explain',
      target: 'draft-orders.detail.send-invoice',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'complete',
      kind: 'explain',
      target: 'draft-orders.detail.complete',
      requires: 'orders.write',
      optional: true,
    },
  ],
  related: ['orders.detail', 'customers.detail'],
});

export const abandonedListTour = defineTour({
  id: 'abandoned.list',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/abandoned-checkouts/',
  scope: 'store',
  requires: 'orders.read',
  icon: ShoppingBag,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'abandoned.list.table' },
        { target: 'abandoned.list.empty', copy: 'empty' },
      ],
    },
    {
      id: 'send',
      kind: 'explain',
      target: 'abandoned.list.send',
      requires: 'orders.write',
      optional: true,
    },
    {
      id: 'settings',
      kind: 'explain',
      target: 'abandoned.list.settings',
      requires: 'settings.read',
    },
  ],
  related: ['abandoned.detail', 'settings.recovery'],
});

export const abandonedDetailTour = defineTour({
  id: 'abandoned.detail',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/abandoned-checkouts/$checkoutId',
  scope: 'record',
  resolve: newestAbandonedCheckout,
  requires: 'orders.read',
  icon: ShoppingBag,
  steps: [
    { id: 'lines', kind: 'explain', target: 'abandoned.detail.lines', placement: 'right' },
    { id: 'contact', kind: 'explain', target: 'abandoned.detail.contact', placement: 'left' },
    { id: 'recovery', kind: 'explain', target: 'abandoned.detail.recovery', placement: 'top' },
    {
      id: 'send',
      kind: 'explain',
      target: 'abandoned.detail.send',
      requires: 'orders.write',
      optional: true,
    },
  ],
  related: ['abandoned.list'],
});

export const returnsListTour = defineTour({
  id: 'returns.list',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/returns/',
  scope: 'store',
  requires: 'orders.read',
  icon: Undo2,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [{ target: 'returns.list.table' }, { target: 'returns.list.empty', copy: 'empty' }],
    },
    {
      id: 'search',
      kind: 'action',
      target: 'returns.list.search',
      advanceOn: { input: true },
      optional: true,
    },
    { id: 'settings', kind: 'explain', target: 'returns.list.settings', requires: 'settings.read' },
  ],
  related: ['returns.detail', 'settings.returns'],
});

export const returnDetailTour = defineTour({
  id: 'returns.detail',
  version: 1,
  group: 'orders',
  route: '/_authed/_active/s/$storeId/returns/$returnId',
  scope: 'record',
  resolve: newestReturn,
  requires: 'orders.read',
  icon: Undo2,
  steps: [
    { id: 'header', kind: 'explain', target: 'returns.detail.header' },
    { id: 'lines', kind: 'explain', target: 'returns.detail.lines', placement: 'right' },
    {
      id: 'hint',
      kind: 'explain',
      target: 'returns.detail.hint',
      requires: 'returns.write',
      optional: true,
    },
    {
      id: 'actions',
      kind: 'explain',
      target: 'returns.detail.actions',
      requires: 'returns.write',
      optional: true,
    },
    {
      id: 'resolve',
      kind: 'explain',
      target: 'returns.detail.resolve',
      requires: 'returns.write',
      optional: true,
    },
  ],
  related: ['returns.list', 'gift-cards.list'],
});
